import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, BellOff, Loader2, Send, Share } from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function urlB64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const b64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

const isIosDevice = () => /iPad|iPhone|iPod/.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isStandalone = () => window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone === true;
const DEFAULT_PREFS = { replies: true, likes: true, new_topics: true };

export default function PushNotificationToggle() {
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [denied, setDenied] = useState(false);

  // Gerçek tarayıcı aboneliğini DB ile senkronize et (yalnızca DB'ye güvenme)
  useEffect(() => {
    const ok = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setSupported(ok);
    if (!ok) return;
    setDenied(Notification.permission === 'denied');
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const browserSub = await reg.pushManager.getSubscription();
        if (browserSub) {
          const json = browserSub.toJSON();
          await axios.post(`${API}/push/subscribe`, { endpoint: json.endpoint, keys: json.keys, prefs: DEFAULT_PREFS }, { withCredentials: true }).catch(() => {});
          setSubscribed(true);
        } else {
          setSubscribed(false);
        }
      } catch {
        setSubscribed(false);
      }
    })();
  }, []);

  const subscribe = async () => {
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        setDenied(perm === 'denied');
        toast.error('Bildirim izni verilmedi. Tarayıcı/telefon ayarlarından bu site için bildirimlere izin ver.');
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const r = await axios.get(`${API}/push/vapid-public-key`);
      const key = r.data.public_key;
      if (!key) throw new Error('VAPID anahtarı bulunamadı');
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(key) });
      }
      const json = sub.toJSON();
      await axios.post(`${API}/push/subscribe`, { endpoint: json.endpoint, keys: json.keys, prefs: DEFAULT_PREFS }, { withCredentials: true });
      setSubscribed(true);
      toast.success('Bildirimler açıldı');
      sendTest(true);
    } catch (e) {
      toast.error('Bildirim açılamadı: ' + (e.message || 'bilinmeyen hata'));
    } finally { setLoading(false); }
  };

  const unsubscribe = async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) await sub.unsubscribe();
      await axios.delete(`${API}/push/subscribe`, { withCredentials: true });
      setSubscribed(false);
      toast.success('Bildirimler kapatıldı');
    } catch {
      toast.error('İşlem başarısız');
    } finally { setLoading(false); }
  };

  const sendTest = async (silent = false) => {
    setTesting(true);
    try {
      const r = await axios.post(`${API}/push/test`, {}, { withCredentials: true });
      if (r.data.sent > 0) {
        if (!silent) toast.success('Test bildirimi gönderildi 🔔 Birazdan görmelisin.');
      } else if (r.data.total === 0) {
        toast.error('Abonelik bulunamadı. Bildirimleri kapatıp tekrar açmayı dene.');
      } else {
        toast.error('Bildirim iletilemedi: ' + (r.data.errors?.[0] || 'push servisi hata döndü'));
      }
    } catch {
      if (!silent) toast.error('Test bildirimi gönderilemedi');
    } finally { setTesting(false); }
  };

  // iOS Safari: push yalnızca Ana Ekrana eklenmiş uygulamada çalışır
  if (!supported) {
    if (!isIosDevice() || isStandalone()) return null;
    return (
      <div className="mt-4 pt-4 border-t border-slate-100 flex items-start gap-2 text-xs text-slate-600" data-testid="push-ios-hint">
        <Bell className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
        <span>
          iPhone/iPad'de bildirimler için siteyi önce <Share className="w-3.5 h-3.5 inline text-emerald-600" /> <b>Paylaş → Ana Ekrana Ekle</b> ile yükle, sonra uygulamadan aç.
        </span>
      </div>
    );
  }

  return (
    <div className="mt-4 pt-4 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center gap-2" data-testid="push-toggle-row">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-800">Anlık bildirimler</div>
        <div className="text-xs text-slate-500">
          {denied && !subscribed
            ? 'Tarayıcı bu site için bildirimleri engellemiş. Adres çubuğundaki kilit/ayarlar simgesinden izin ver.'
            : subscribed ? 'Bu cihazda açık. Yanıt ve beğenilerde haberin olur.' : 'Yanıt ve beğeni geldiğinde anında haberin olsun.'}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant={subscribed ? 'outline' : 'default'}
          size="sm"
          disabled={loading}
          onClick={subscribed ? unsubscribe : subscribe}
          className={`flex-1 sm:flex-none ${subscribed ? 'border-emerald-200 text-emerald-700 hover:bg-emerald-50' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}`}
          data-testid="push-toggle-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : subscribed ? <BellOff className="w-4 h-4 mr-1.5" /> : <Bell className="w-4 h-4 mr-1.5" />}
          {subscribed ? 'Bildirimleri kapat' : 'Bildirimleri aç'}
        </Button>
        {subscribed && (
          <Button
            variant="outline"
            size="sm"
            disabled={testing}
            onClick={() => sendTest(false)}
            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            data-testid="push-test-btn"
            title="Kendine test bildirimi gönder"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            <span className="ml-1.5">Test</span>
          </Button>
        )}
      </div>
    </div>
  );
}
